import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Building2,
  Check,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  FolderTree,
  Globe,
  Landmark,
  Layers,
  Loader2,
  MapPin,
  Pencil,
  Plus,
  Search,
  Settings2,
  Trash2,
} from 'lucide-react';

import { Button } from '../../ComponnentsUI/common/Button';
import { Modal } from '../../ComponnentsUI/common/Modal';
import { Input } from '../../ComponnentsUI/common/Input';
import { Select } from '../../ComponnentsUI/common/Select';
import { ConfirmDialog } from '../../ComponnentsUI/common/ConfirmDialog';
import { StatCard } from '../../ComponnentsUI/common/StatCard';
import {
  OrgTemplate,
  OrgUnitNode,
  OrgUnitType,
  useApplyOrgTemplate,
  useCreateOrgUnit,
  useDeleteOrgUnit,
  useOrgTemplates,
  useOrgTree,
  useUpdateOrgUnit,
} from '../../../helpers/api/org-units/org-units';

const TYPE_LABEL: Record<OrgUnitType, string> = {
  ORG: 'Organization',
  REGION: 'Region',
  PROVINCE: 'Province',
  DIVISION: 'Division',
  FACILITY: 'Facility',
};

type DeploymentShape = 'MOH' | 'MFM' | 'PRIVATE';

const SHAPE_LABEL: Record<DeploymentShape, string> = {
  MOH: 'Ministry of Health',
  MFM: 'MFM Group',
  PRIVATE: 'Private Practice',
};

const SHAPE_CHILDREN: Record<DeploymentShape, Partial<Record<OrgUnitType, OrgUnitType[]>>> = {
  MOH: {
    ORG: ['REGION'],
    REGION: ['PROVINCE'],
    PROVINCE: ['FACILITY'],
  },
  MFM: {
    ORG: ['DIVISION'],
    DIVISION: ['FACILITY'],
  },
  PRIVATE: {
    ORG: ['FACILITY'],
  },
};

const ALL_CHILD_TYPES: OrgUnitType[] = [
  'REGION',
  'PROVINCE',
  'DIVISION',
  'FACILITY',
];

const typeOptionLabel = (t: OrgUnitType) =>
  t === 'FACILITY' ? 'Facility (holds patients)' : TYPE_LABEL[t];

type ModalState =
  | { mode: 'create-child'; parent: OrgUnitNode }
  | { mode: 'edit'; node: OrgUnitNode }
  | null;

// Icon + accent color per unit type, purely decorative.
const TYPE_STYLE: Record<OrgUnitType, { icon: React.ElementType; chip: string }> = {
  ORG: {
    icon: Landmark,
    chip: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  },
  REGION: {
    icon: Globe,
    chip: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
  },
  PROVINCE: {
    icon: MapPin,
    chip: 'bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
  },
  DIVISION: {
    icon: FolderTree,
    chip: 'bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400',
  },
  FACILITY: {
    icon: Building2,
    chip: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
  },
};

function TypeChip({ type }: { type: OrgUnitType }) {
  const { icon: Icon, chip } = TYPE_STYLE[type];
  return (
    <span
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${chip}`}
      title={TYPE_LABEL[type]}
    >
      <Icon size={15} />
    </span>
  );
}

// ---- Tree helpers (display only) ------------------------------------------

/** Count direct children and total facilities in a subtree (excluding the node itself). */
function subtreeCounts(node: OrgUnitNode): { units: number; facilities: number } {
  let units = 0;
  let facilities = 0;
  const walk = (n: OrgUnitNode) => {
    (n.children ?? []).forEach(c => {
      units += 1;
      if (c.type === 'FACILITY') facilities += 1;
      walk(c);
    });
  };
  walk(node);
  return { units, facilities };
}

/**
 * Keep nodes whose name matches the query, plus all their ancestors (so the
 * path to a match stays visible). Returns null when nothing in the subtree matches.
 */
function filterTree(node: OrgUnitNode, q: string): OrgUnitNode | null {
  const selfMatch = node.name.toLowerCase().includes(q);
  const children = (node.children ?? [])
    .map(c => filterTree(c, q))
    .filter((c): c is OrgUnitNode => c !== null);
  if (!selfMatch && children.length === 0) return null;
  return { ...node, children };
}

function collectExpandableIds(node: OrgUnitNode, acc: string[] = []): string[] {
  if (node.children?.length) {
    acc.push(node.id);
    node.children.forEach(c => collectExpandableIds(c, acc));
  }
  return acc;
}

/** Node name with the matched part of the search query highlighted. */
function HighlightedName({ name, query }: { name: string; query: string }) {
  if (!query) {
    return <>{name}</>;
  }
  const idx = name.toLowerCase().indexOf(query);
  if (idx === -1) {
    return <>{name}</>;
  }
  return (
    <>
      {name.slice(0, idx)}
      <mark className="rounded bg-yellow-100 px-0.5 text-inherit dark:bg-yellow-500/30">
        {name.slice(idx, idx + query.length)}
      </mark>
      {name.slice(idx + query.length)}
    </>
  );
}

function NodeRow({
  node,
  depth,
  query,
  collapsed,
  onToggle,
  onAddChild,
  onEdit,
  onDelete,
}: {
  node: OrgUnitNode;
  depth: number;
  query: string;
  collapsed: Set<string>;
  onToggle: (id: string) => void;
  onAddChild: (n: OrgUnitNode) => void;
  onEdit: (n: OrgUnitNode) => void;
  onDelete: (n: OrgUnitNode) => void;
}) {
  const isFacility = node.type === 'FACILITY';
  const hasChildren = Boolean(node.children?.length);
  // While searching, force everything open so matches are always visible.
  const isExpanded = query ? true : !collapsed.has(node.id);
  const counts = hasChildren ? subtreeCounts(node) : null;

  return (
    <div>
      <div
        className={`group flex items-center justify-between gap-2 rounded-lg px-2 py-2 hover:bg-card-color-200/60 dark:hover:bg-gray-700/50 ${
          hasChildren && !query ? 'cursor-pointer' : ''
        }`}
        onClick={() => hasChildren && !query && onToggle(node.id)}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          {hasChildren ? (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                onToggle(node.id);
              }}
              disabled={Boolean(query)}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
              aria-expanded={isExpanded}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-title-page-color-300 hover:bg-card-color-200 dark:text-gray-400 dark:hover:bg-gray-700 disabled:opacity-40"
            >
              <ChevronRight
                size={15}
                className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              />
            </button>
          ) : (
            <span className="w-6 shrink-0" />
          )}
          <TypeChip type={node.type} />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate font-medium text-title-page-color-100 dark:text-white">
                <HighlightedName name={node.name} query={query} />
              </span>
              {node.is_phi_scoped && (
                <span
                  className="shrink-0 rounded-md bg-green-50 dark:bg-green-900/30 px-1.5 py-0.5 text-[10px] font-semibold text-green-700 dark:text-green-300"
                  title="Holds patient data (PHI)"
                >
                  PHI
                </span>
              )}
            </div>
            <div className="truncate text-xs text-title-page-color-300 dark:text-gray-500">
              {TYPE_LABEL[node.type]}
              {counts &&
                ` · ${counts.units} unit${counts.units === 1 ? '' : 's'}${
                  counts.facilities > 0
                    ? `, ${counts.facilities} facilit${counts.facilities === 1 ? 'y' : 'ies'}`
                    : ''
                }`}
            </div>
          </div>
        </div>
        <div
          className="flex shrink-0 items-center gap-0.5 opacity-60 transition-opacity focus-within:opacity-100 group-hover:opacity-100"
          onClick={e => e.stopPropagation()}
        >
          {!isFacility && (
            <Button variant="ghost" size="sm" onClick={() => onAddChild(node)} title="Add child">
              <Plus size={16} />
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => onEdit(node)} title="Edit">
            <Pencil size={16} />
          </Button>
          {depth > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(node)}
              title="Delete"
              className="text-red-500 hover:text-red-600"
            >
              <Trash2 size={16} />
            </Button>
          )}
        </div>
      </div>
      {hasChildren && isExpanded && (
        <div className="ml-[11px] border-l border-gray-200 pl-3 dark:border-gray-700">
          {node.children!.map(child => (
            <NodeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              query={query}
              collapsed={collapsed}
              onToggle={onToggle}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const OrgStructurePage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { data: tree, isLoading, refetch } = useOrgTree();
  const [modal, setModal] = useState<ModalState>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Collapse state survives navigation so big trees stay the way the user left them.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('org-structure:collapsed');
      return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      return new Set();
    }
  });
  useEffect(() => {
    localStorage.setItem('org-structure:collapsed', JSON.stringify(Array.from(collapsed)));
  }, [collapsed]);
  const [deleteTarget, setDeleteTarget] = useState<OrgUnitNode | null>(null);

  const deleteUnit = useDeleteOrgUnit(() => {
    setDeleteTarget(null);
    refetch();
  });

  const query = search.trim().toLowerCase();
  // null while searching means nothing (not even the root) matched.
  const visibleTree = useMemo(() => {
    if (!tree) return null;
    if (!query) return tree;
    return filterTree(tree, query);
  }, [tree, query]);

  const toggleNode = (id: string) =>
    setCollapsed(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => tree && setCollapsed(new Set(collectExpandableIds(tree)));

  // Headline numbers for the whole structure (root excluded from unit count).
  const stats = useMemo(() => {
    if (!tree) return null;
    const { units, facilities } = subtreeCounts(tree);
    let depth = 0;
    const walk = (n: OrgUnitNode, d: number) => {
      depth = Math.max(depth, d);
      (n.children ?? []).forEach(c => walk(c, d + 1));
    };
    walk(tree, 1);
    return { units, facilities, depth };
  }, [tree]);

  // The tree endpoint serializes the JSON column as `meta` (Pydantic alias);
  // fall back to `metadata` defensively.
  const treeMeta = (tree?.meta ?? tree?.metadata) as Record<string, unknown> | undefined;
  const rawShape = treeMeta?.shape as DeploymentShape | 'CUSTOM' | undefined;
  const hasShape = Boolean(rawShape);
  const shape: DeploymentShape =
    rawShape && rawShape !== 'CUSTOM' ? (rawShape as DeploymentShape) : 'PRIVATE';

  // The template picker is a setup tool: it's only relevant for a brand-new org
  // (no nodes built yet). Once a structure exists, it has no purpose, so we never
  // auto-open it or surface it - the tab is just the tree builder.
  const isEmptyOrg = (tree?.children?.length ?? 0) === 0;
  const needsSetup = isEmptyOrg && !hasShape;

  // First-time setup only: open the picker automatically for a fresh, empty org.
  useEffect(() => {
    if (tree && needsSetup) setPickerOpen(true);
  }, [tree, needsSetup]);

  // If the owner ignored the picker and started building, the structure is, by
  // definition, custom - record it (once) so it's no longer "unset".
  const applyTemplate = useApplyOrgTemplate(() => refetch());
  const autoCustomRef = useRef(false);
  useEffect(() => {
    if (tree && !isEmptyOrg && !hasShape && !autoCustomRef.current) {
      autoCustomRef.current = true;
      applyTemplate.mutate('CUSTOM');
    }
  }, [tree, isEmptyOrg, hasShape]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeModal = () => setModal(null);
  const onDone = () => {
    closeModal();
    refetch();
  };

  return (
    <div className={embedded ? '' : 'p-6'}>
      {/* Standalone route only: inside the Organization tabs the active tab
          already provides this context, so a repeated big title is just noise. */}
      {!embedded && (
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-100 p-2 dark:bg-blue-900/30">
              <FolderTree size={24} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-title-page-color-100 dark:text-white">
                Organization Structure
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Build the org tree under your organization. Only{' '}
                <span className="font-medium">Facility</span> nodes hold patients; everything
                above is administrative.
              </p>
            </div>
          </div>
        </div>
      )}
      {tree && isEmptyOrg && (
        <div className="mb-4 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => setPickerOpen(true)}
          >
            <Settings2 size={15} className="mr-1.5" />
            Choose structure
          </Button>
        </div>
      )}

      {tree && !isEmptyOrg && stats && (
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3 animate-in fade-in">
          <StatCard
            title="Organizational units"
            value={String(stats.units)}
            icon={<FolderTree size={20} />}
            color="#3b82f6"
          />
          <StatCard
            title="Facilities (hold patients)"
            value={String(stats.facilities)}
            icon={<Building2 size={20} />}
            color="#10b981"
          />
          <StatCard
            title="Hierarchy levels"
            value={String(stats.depth)}
            icon={<Layers size={20} />}
            color="#8b5cf6"
          />
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 bg-card-color-100 p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        {isLoading ? (
          // Skeleton rows shaped like the tree, so the card keeps a sensible
          // height while loading instead of a full-screen spinner.
          <div className="animate-pulse space-y-2 py-1" aria-label="Loading organization structure">
            {[0, 1, 2, 3, 4].map(i => (
              <div
                key={i}
                className="flex items-center gap-2.5 px-2 py-2"
                style={{ marginLeft: i === 0 ? 0 : 24 }}
              >
                <span className="h-8 w-8 shrink-0 rounded-lg bg-gray-200 dark:bg-gray-700" />
                <div className="flex-1 space-y-1.5">
                  <div
                    className="h-3.5 rounded bg-gray-200 dark:bg-gray-700"
                    style={{ maxWidth: 180 - i * 15 }}
                  />
                  <div
                    className="h-2.5 rounded bg-gray-100 dark:bg-gray-700/60"
                    style={{ maxWidth: 100 }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : !tree ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <FolderTree size={32} className="text-title-page-color-300 dark:text-gray-500" />
            <p className="text-sm text-title-page-color-300 dark:text-gray-400">
              Setting up your organization…
            </p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <>
            {!isEmptyOrg && (
              <div className="mb-3 flex flex-col gap-2 border-b border-gray-100 pb-3 dark:border-gray-700/60 sm:flex-row sm:items-center">
                <div className="relative flex-1 sm:max-w-xs">
                  <Search
                    size={15}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-title-page-color-300 dark:text-gray-500"
                  />
                  <Input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search units…"
                    className="h-9 pl-9"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={expandAll}
                    disabled={Boolean(query)}
                    title="Expand all"
                  >
                    <ChevronsUpDown size={15} className="mr-1.5" />
                    Expand all
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={collapseAll}
                    disabled={Boolean(query)}
                    title="Collapse all"
                  >
                    <ChevronsDownUp size={15} className="mr-1.5" />
                    Collapse all
                  </Button>
                </div>
              </div>
            )}

            {query && !visibleTree ? (
              <div className="flex flex-col items-center gap-2 py-10 text-center">
                <Search size={28} className="text-title-page-color-300 dark:text-gray-500" />
                <p className="text-sm text-title-page-color-300 dark:text-gray-400">
                  No unit matches “{search.trim()}”.
                </p>
                <Button variant="ghost" size="sm" onClick={() => setSearch('')}>
                  Clear search
                </Button>
              </div>
            ) : (
              <NodeRow
                node={visibleTree ?? tree}
                depth={0}
                query={query}
                collapsed={collapsed}
                onToggle={toggleNode}
                onAddChild={n => setModal({ mode: 'create-child', parent: n })}
                onEdit={n => setModal({ mode: 'edit', node: n })}
                onDelete={n => setDeleteTarget(n)}
              />
            )}

            {isEmptyOrg && (
              <div className="mt-1 flex flex-col items-center gap-2 rounded-lg border border-dashed border-gray-200 py-8 text-center dark:border-gray-700">
                <FolderTree size={28} className="text-title-page-color-300 dark:text-gray-500" />
                <p className="text-sm text-title-page-color-300 dark:text-gray-400">
                  No units yet. Use <span className="font-medium">+</span> on your organization to
                  add the first region, division or facility.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {modal && (
        <OrgUnitModal state={modal} shape={shape} onClose={closeModal} onDone={onDone} />
      )}

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        title={`Delete "${deleteTarget?.name ?? ''}"?`}
        message={
          deleteTarget ? (
            <>
              This will permanently remove the{' '}
              <span className="font-medium">{TYPE_LABEL[deleteTarget.type].toLowerCase()}</span>{' '}
              “{deleteTarget.name}”. Units with children, assigned staff or patients cannot be
              deleted.
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Delete"
        isPending={deleteUnit.isPending}
        onConfirm={() => deleteTarget && deleteUnit.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />

      {pickerOpen && (
        <OrgTemplatePicker
          currentShape={rawShape}
          onClose={() => setPickerOpen(false)}
          onApplied={() => {
            setPickerOpen(false);
            refetch();
          }}
        />
      )}
    </div>
  );
};

// Small visual of a template's level chain (Organization -> ... -> Facility).
function LevelChain({ levels }: { levels: OrgUnitType[] }) {
  return (
    <div className="mt-3 space-y-1">
      {levels.map((lvl, i) => (
        <div
          key={`${lvl}-${i}`}
          className="flex items-center gap-1.5 text-xs"
          style={{ marginLeft: i * 14 }}
        >
          <span className="text-gray-300 dark:text-gray-600">
            {i === 0 ? '' : '└'}
          </span>
          <span
            className={`rounded px-1.5 py-0.5 font-medium ${
              lvl === 'FACILITY'
                ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
            }`}
          >
            {TYPE_LABEL[lvl]}
          </span>
          {lvl === 'FACILITY' && (
            <span className="text-[10px] text-green-600 dark:text-green-400">holds patients</span>
          )}
        </div>
      ))}
    </div>
  );
}

function OrgTemplatePicker({
  currentShape,
  onClose,
  onApplied,
}: {
  currentShape?: string;
  onClose: () => void;
  onApplied: (shape: string) => void;
}) {
  const { data: tpl, isLoading } = useOrgTemplates();
  const apply = useApplyOrgTemplate((_res, shape) => onApplied(shape));
  const [selected, setSelected] = useState<string | undefined>(currentShape);

  const shapes: OrgTemplate[] = tpl?.shapes ?? [];

  const Card = ({
    active,
    onClick,
    children,
  }: {
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex flex-col items-stretch justify-start text-left rounded-xl border p-4 transition ${
        active
          ? 'border-blue-500 ring-2 ring-blue-500/30 bg-blue-50/40 dark:bg-blue-900/20'
          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
      }`}
    >
      {active && (
        <span className="absolute right-3 top-3 text-blue-600 dark:text-blue-400">
          <Check size={16} />
        </span>
      )}
      {children}
    </button>
  );

  return (
    <Modal isOpen onClose={onClose} size="xl">
      <div className="p-6">
        <h3 className="text-lg font-bold text-title-page-color-100 dark:text-white">
          Choose your organization structure
        </h3>
        <p className="mt-1 text-sm text-title-page-color-300 dark:text-gray-400">
          Pick a template that matches your organization, or build your own. The top is always your
          Organization and the bottom is always a Facility (which holds patients).
        </p>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-14 text-title-page-color-300 dark:text-gray-400">
            <Loader2 size={24} className="animate-spin text-blue-600" />
            <p className="text-sm">Loading templates…</p>
          </div>
        ) : (
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            {shapes.map(s => (
              <Card key={s.key} active={selected === s.key} onClick={() => setSelected(s.key)}>
                <div className="flex items-center gap-2">
                  <Building2 size={18} className="text-blue-600 dark:text-blue-400" />
                  <span className="font-semibold text-title-page-color-100 dark:text-white">
                    {s.label}
                  </span>
                </div>
                <p className="mt-1 min-h-[3.5rem] text-xs text-title-page-color-300 dark:text-gray-400">
                  {s.description}
                </p>
                <LevelChain levels={s.levels} />
              </Card>
            ))}

            <Card active={selected === 'CUSTOM'} onClick={() => setSelected('CUSTOM')}>
              <div className="flex items-center gap-2">
                <Settings2 size={18} className="text-blue-600 dark:text-blue-400" />
                <span className="font-semibold text-title-page-color-100 dark:text-white">
                  Custom
                </span>
              </div>
              <p className="mt-1 min-h-[3.5rem] text-xs text-title-page-color-300 dark:text-gray-400">
                Build your own hierarchy. Organization at the top, Facility at the bottom, and your
                own levels (Region, Province, Division) in between - in any
                arrangement.
              </p>
              <LevelChain levels={['ORG', 'FACILITY'] as OrgUnitType[]} />
            </Card>
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-3">
          <Button variant="secondary" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="md"
            onClick={() => selected && apply.mutate(selected)}
            disabled={!selected || apply.isPending}
          >
            {apply.isPending ? 'Applying…' : 'Apply'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function OrgUnitModal({
  state,
  shape,
  onClose,
  onDone,
}: {
  state: NonNullable<ModalState>;
  shape: DeploymentShape;
  onClose: () => void;
  onDone: () => void;
}) {
  const editing = state.mode === 'edit';
  const isRoot = editing && state.node.parent_id == null;
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Free structure: any non-Facility node may have any of these children. The
  // deployment-shape template only suggests the usual next level (the default).
  const suggestedChildTypes =
    state.mode === 'create-child' ? SHAPE_CHILDREN[shape][state.parent.type] ?? [] : [];
  const initialName = editing ? state.node.name : '';
  const [name, setName] = useState(initialName);
  const [type, setType] = useState<OrgUnitType>(
    editing ? state.node.type : suggestedChildTypes[0] ?? 'FACILITY'
  );

  const create = useCreateOrgUnit(onDone);
  const update = useUpdateOrgUnit(editing ? state.node.id : '', onDone);
  const remove = useDeleteOrgUnit(onDone);

  const title = useMemo(() => {
    if (state.mode === 'create-child') return `Add node under "${state.parent.name}"`;
    return `Edit "${state.node.name}"`;
  }, [state]);

  const canSubmit = Boolean(name.trim()) && !create.isPending && !update.isPending;
  const submit = () => {
    if (!canSubmit) return;
    if (state.mode === 'edit') {
      update.mutate({ name });
      return;
    }
    create.mutate({
      type,
      name,
      parent_id: state.parent.id,
      is_phi_scoped: type === 'FACILITY',
    });
  };

  return (
    <Modal isOpen onClose={onClose} size="md">
      <div className="p-6">
        <h3 className="mb-4 text-base font-bold text-title-page-color-100 dark:text-white">
          {title}
        </h3>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-title-page-color-300 dark:text-gray-300">
              Name
            </label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && submit()}
              placeholder="e.g. CSR Aghbala"
              autoFocus
            />
          </div>

          {state.mode === 'create-child' && (
            <div>
              <label className="mb-1 block text-sm font-medium text-title-page-color-300 dark:text-gray-300">
                Type
              </label>
              <Select value={type} onChange={e => setType(e.target.value as OrgUnitType)}>
                {ALL_CHILD_TYPES.map(t => {
                  const suggested = suggestedChildTypes.includes(t);
                  return (
                    <option key={t} value={t}>
                      {typeOptionLabel(t)}
                      {suggested ? ` - suggested (${SHAPE_LABEL[shape]})` : ''}
                    </option>
                  );
                })}
              </Select>
              <p className="mt-1 text-xs text-title-page-color-300 dark:text-gray-400">
                {type === 'FACILITY'
                  ? 'A Facility holds patients (PHI) and cannot have children.'
                  : 'A container groups other nodes - administration & KPIs only, no patients.'}
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between">
          {editing && !isRoot ? (
            <Button
              variant="destructive"
              onClick={() => setConfirmDelete(true)}
              disabled={remove.isPending}
            >
              <Trash2 size={16} className="mr-1" /> Delete
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={!canSubmit}>
              {editing ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </div>

      {editing && (
        <ConfirmDialog
          isOpen={confirmDelete}
          title={`Delete "${state.node.name}"?`}
          message={
            <>
              This will permanently remove the{' '}
              <span className="font-medium">{TYPE_LABEL[state.node.type].toLowerCase()}</span>{' '}
              “{state.node.name}”. Units with children, assigned staff or patients cannot be
              deleted.
            </>
          }
          confirmLabel="Delete"
          isPending={remove.isPending}
          onConfirm={() => remove.mutate(state.node.id)}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </Modal>
  );
}

export default OrgStructurePage;
